import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Heart, Car, Users, Clock, Shield, Phone, Mail, CheckCircle2, Star, Camera, Music, Sparkles, ArrowRight
} from "lucide-react";
import ContactPopup from "../../components/ContactPopup";
import PageTransition from "../../components/PageTransition";

const WeddingsPage = () => {
  const services = [
    {
      icon: Car,
      title: "Bridal Car Service",
      description: "Arrive in style with our beautifully decorated luxury vehicles. Red carpet service and champagne toast included."
    },
    {
      icon: Users,
      title: "Bridal Party Transport",
      description: "Coordinate your entire bridal party with our Mercedes Sprinters and luxury people movers."
    },
    {
      icon: Camera,
      title: "Photo Opportunities",
      description: "We allow extra time for photos with our stunning vehicles at your chosen locations."
    },
    {
      icon: Clock,
      title: "Flexible Timing",
      description: "Your chauffeur stays with you throughout the day, ensuring seamless transitions between venues."
    },
    {
      icon: Sparkles,
      title: "Vehicle Decoration",
      description: "Complimentary ribbons, bows, and floral arrangements to match your wedding theme."
    },
    {
      icon: Shield,
      title: "Backup Guarantee",
      description: "Peace of mind with our backup vehicle guarantee. We never leave you stranded on your special day."
    }
  ];

  const packages = [
    {
      name: "Classic",
      price: "From $450",
      features: [
        "2 hours of service",
        "Luxury sedan or SUV",
        "Professional chauffeur",
        "Ribbon decoration",
        "Complimentary water"
      ]
    },
    {
      name: "Premium",
      price: "From $750",
      features: [
        "3 hours of service",
        "Mercedes S-Class or similar",
        "Red carpet service",
        "Champagne toast",
        "Full vehicle decoration",
        "Photo opportunity stops"
      ],
      popular: true
    },
    {
      name: "Grand",
      price: "From $1,200",
      features: [
        "8 hours of service",
        "Choice of premium vehicle",
        "Bridal party transport included",
        "Multiple venue transfers",
        "Champagne & refreshments*",
        "Custom decoration theme*",
        "Dedicated coordinator"
      ],
      note: "*Extra charges apply for Champagne & refreshments and Custom decoration theme"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Wedding Car Hire & Bridal Transport - Limo Near Me",
    "description": "Luxury wedding car hire and bridal transport services across Australia. Elegant vehicles, professional chauffeurs, red carpet service for your special day.",
    "provider": {
      "@type": "Organization",
      "name": "Limo Near Me",
      "telephone": "+61481437772"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Australia"
    },
    "serviceType": "Wedding Transportation"
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Wedding Car Hire Australia | Luxury Bridal Transport | Limo Near Me</title>
          <meta name="description" content="Luxury wedding car hire and bridal transport services. Elegant Mercedes, decorated vehicles, red carpet service. Brisbane, Sydney, Melbourne, Gold Coast wedding limos. Book your special day transport." />
          <meta name="keywords" content="wedding car hire, bridal car service, wedding limo Australia, wedding transport Brisbane, Sydney wedding cars, Melbourne bridal transport, luxury wedding vehicles, wedding chauffeur" />
          <link rel="canonical" href="https://www.limonearme.com.au/services/weddings" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/corporate-travel" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Corporate</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                  <Heart className="h-4 w-4" />
                  <span className="text-sm">Wedding Transport</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Make Your Wedding Day Unforgettable
                </h1>
                <p className="text-xl text-gray-300 mb-8">
                  Arrive in elegance and style with our luxury wedding car service. 
                  Professional chauffeurs, beautifully decorated vehicles, and impeccable service for your special day.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/book">
                    <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8">
                      Get a Wedding Quote
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <a href="tel:+61481437772">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8">
                      <Phone className="mr-2 h-4 w-4" />
                      Speak to Us
                    </Button>
                  </a>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="hidden md:block"
              >
                <img 
                  src="https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop" 
                  alt="Wedding car service" 
                  className="rounded-2xl shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Our Wedding Services</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every detail matters on your wedding day. We ensure your transport is perfect.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-xl border border-black/5 hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-pink-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Wedding Packages</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Choose the perfect package for your special day, or let us create a custom solution.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {packages.map((pkg, index) => (
                <motion.div
                  key={pkg.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-white rounded-2xl p-8 ${pkg.popular ? 'ring-2 ring-primary shadow-xl' : 'border border-black/5'}`}
                >
                  {pkg.popular && (
                    <div className="text-center mb-4">
                      <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-center mb-2">{pkg.name}</h3>
                  <p className="text-sm font-semibold text-center text-primary mb-6">Custom quoted for your day</p>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {pkg.note && (
                    <p className="text-xs text-muted-foreground mb-4 italic">{pkg.note}</p>
                  )}
                  <Link to="/book">
                    <Button className="w-full" variant={pkg.popular ? "default" : "outline"}>
                      Enquire Now
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <blockquote className="text-2xl font-medium text-foreground mb-6 italic">
              "Our wedding day was perfect thanks to Limo Near Me. The car was stunning, 
              the chauffeur was incredibly professional, and everything ran like clockwork. 
              Highly recommend!"
            </blockquote>
            <p className="text-muted-foreground">— Jessica & Michael, Brisbane Wedding</p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Heart className="h-12 w-12 mx-auto mb-6 text-pink-400" />
            <h2 className="text-3xl font-bold mb-4">Let's Plan Your Perfect Wedding Transport</h2>
            <p className="text-gray-400 mb-8">
              Contact us today to discuss your wedding day requirements. We'll create a custom package just for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8">
                  Get a Free Quote
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 hover:bg-white/10">
                  <Phone className="h-4 w-4 mr-2" />
                  Call +61 481 437 772
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default WeddingsPage;
